import { Link } from 'react-router-dom';
import { Users, ArrowRight, Ticket, Zap, BadgeCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { FORMAT_BADGE_STYLES } from '@/lib/constants';
import { DeadlineBadge } from '@/components/ui/DeadlineBadge';
import { LiveBadge } from '@/components/races/LiveBadge';
import { getDeadlineInfo } from '@/lib/deadline';
import type { RaceFormat } from '@/gql/graphql';

interface RaceCardProps {
    id: string;
    name: string;
    raceFormat?: RaceFormat | null;
    bettingDeadline: string;
    runnerCount: number;
    bettorCount: number;
    isOfficial?: boolean | null;
    /** Extra badges rendered top-right */
    badges?: React.ReactNode;
    /** Accent class override (default: emerald border on hover) */
    accentClass?: string;
    /** Show "Bet Now" urgency badge when < 48h and no user bet */
    showBetNow?: boolean;
    /** Show live indicator */
    isLive?: boolean;
}

export function RaceCard({
    id,
    name,
    raceFormat,
    bettingDeadline,
    runnerCount,
    bettorCount,
    isOfficial,
    badges,
    accentClass,
    showBetNow,
    isLive,
}: RaceCardProps) {
    const { t } = useTranslation('races');

    const liveAccent = isLive
        ? "border-rose-200 hover:border-rose-300 bg-rose-50/20"
        : undefined;
    const officialAccent = !isLive && isOfficial
        ? "border-amber-200 hover:border-amber-300 bg-amber-50/20 official-card"
        : undefined;

    const deadlineInfo = getDeadlineInfo(bettingDeadline);

    return (
        <Link
            to={`/races/${id}`}
            className={cn(
                "group bg-white p-4 rounded-2xl border shadow-sm hover:shadow-md transition-all relative overflow-hidden flex flex-col h-full",
                liveAccent ?? officialAccent ?? accentClass ?? "border-slate-100 hover:border-emerald-100",
            )}
        >
            {/* Header: name + badges */}
            <div className="mb-2">
                <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-bold text-slate-900 group-hover:text-emerald-700 transition-colors line-clamp-1 leading-snug">
                        {name}
                    </h3>
                    {badges}
                </div>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    {raceFormat && FORMAT_BADGE_STYLES[raceFormat] && (
                        <span
                            className="shrink-0 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase"
                            style={{ backgroundColor: FORMAT_BADGE_STYLES[raceFormat].bg, color: FORMAT_BADGE_STYLES[raceFormat].text }}
                        >
                            {FORMAT_BADGE_STYLES[raceFormat].label}
                        </span>
                    )}
                    {isOfficial && (
                        <span className="shrink-0 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-0.5">
                            <BadgeCheck size={9} />
                            {t('details.officialBadge')}
                        </span>
                    )}
                    {isLive && <LiveBadge />}
                </div>
            </div>

            {/* Footer: stats + deadline */}
            <div className="flex items-center gap-3 text-xs text-slate-400 mt-auto pt-2 flex-wrap">
                <span className="flex items-center gap-1">
                    <Users size={12} />
                    {t('dashboard.runners', { count: runnerCount })}
                </span>
                {bettorCount > 0 ? (
                    <span className={cn("flex items-center gap-1", isOfficial && "font-semibold text-amber-600")}>
                        <Ticket size={12} />
                        {t('dashboard.bettors', { count: bettorCount })}
                    </span>
                ) : !deadlineInfo.passed && (
                    <span className="flex items-center gap-1 font-semibold text-emerald-600">
                        <Zap size={12} />
                        {t('dashboard.firstToBet')}
                    </span>
                )}
                <DeadlineBadge deadline={bettingDeadline} className="text-xs" />
                {showBetNow && !deadlineInfo.passed && deadlineInfo.hours < 48 && (
                    <span className="ml-auto text-[10px] font-bold uppercase tracking-wide text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                        {t('dashboard.betNow')}
                    </span>
                )}
            </div>

            {/* Hover arrow */}
            <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-all translate-x-1 group-hover:translate-x-0">
                <div className={cn(
                    "w-6 h-6 rounded-full flex items-center justify-center",
                    isLive ? "bg-rose-100 text-rose-600" : isOfficial ? "bg-amber-100 text-amber-600" : "bg-emerald-100 text-emerald-600",
                )}>
                    <ArrowRight size={12} />
                </div>
            </div>
        </Link>
    );
}
